/**
 * Port Relationships - Creates HAS_PORT relationship structures
 *
 * Handles creation of port-based relationships for fields that reference
 * Port nodes in the graph database (port numbers and protocols/services).
 *
 * @module queryBuilderCore/relationships/portRelationships
 */

import {
  LogicalOperator,
  QueryFilter,
  Relationship,
} from '@/sections/insights/queryBuilder/legacy/types/types';

/**
 * Maps frontend field names to Port node properties
 */
export const PORT_FIELD_MAP: Record<string, string> = {
  port: 'port', // Port number (e.g., 22, 443)
  protocol: 'service', // Protocol/service name (e.g., "http", "ssh")
};

/**
 * Creates a single QueryFilter for port-based fields
 *
 * This is the core filter creation logic used by both createPortRelationships()
 * and external callers like buildPortFilters() in useQueryTechnologyAssets.
 *
 * Handles:
 * - Field mapping: "protocol" → "service", "port" → "port"
 * - Operator normalization: "IN"/"NOT IN" → "="
 * - Type conversion: port values to numbers, protocol values to strings
 *
 * @param field - Port field name ("port" or "protocol")
 * @param operator - Filter operator (will normalize IN/NOT IN to =)
 * @param value - Filter value(s)
 * @param isNot - Whether to negate the filter
 * @returns QueryFilter for the port field
 */
export function createPortNodeFilter(
  field: string,
  operator: string,
  value: string | string[] | number | number[],
  isNot = false
): QueryFilter {
  // Map frontend field names to Port node properties
  const portNodeField = PORT_FIELD_MAP[field] || field;

  // Convert value to appropriate type
  let portValue: string | string[] | number | number[];
  if (field === 'port') {
    // Port numbers should be numbers
    portValue = Array.isArray(value)
      ? value.map(v => Number(v))
      : Number(value);
  } else {
    // Protocols should be strings
    portValue = Array.isArray(value)
      ? value.map(v => String(v))
      : String(value);
  }

  // Normalize operators: IN/NOT IN → = for Neo4j compatibility with array values
  let finalOperator = operator;
  if (operator === 'IN' || operator === 'NOT IN') {
    finalOperator = '=';
  }

  return {
    field: portNodeField,
    operator: finalOperator,
    value: portValue,
    ...(isNot && { not: true }),
  };
}

/**
 * Creates HAS_PORT relationships for port-based fields
 *
 * Maps frontend field names to Port node properties:
 * - "port" → Port.port (port number)
 * - "protocol" → Port.service (protocol/service name like "http", "ssh", etc.)
 *
 * @param field - Port field name ("port" or "protocol")
 * @param operator - Filter operator
 * @param value - Filter value(s)
 * @param groupLogicalOperator - Logical operator for the filter group (AND/OR)
 * @param isNot - Whether to negate the filter
 * @returns Array of relationship structures for the port
 */
export function createPortRelationships(
  field: string,
  operator: string,
  value: string | string[] | number | number[] | boolean | boolean[],
  groupLogicalOperator: LogicalOperator,
  isNot = false
): Relationship[] {
  const relationships: Relationship[] = [];

  // Map frontend field names to Port node properties
  const portFieldMap: Record<string, string> = {
    port: 'port', // Port number (e.g., 22, 443)
    protocol: 'service', // Protocol/service name (e.g., "http", "ssh")
  };

  const portNodeField = portFieldMap[field] || field;

  // Convert value to appropriate type
  // Port numbers should be numbers, protocols should be strings
  let portValue: string | string[] | number | number[];
  if (field === 'port') {
    // Port numbers should be numbers
    portValue = Array.isArray(value)
      ? value.map(v => Number(v))
      : Number(value);
  } else {
    // Protocols should be strings
    portValue = Array.isArray(value)
      ? value.map(v => String(v))
      : String(value);
  }

  const shouldCreateSeparateRelationships =
    Array.isArray(portValue) &&
    portValue.length > 1 &&
    groupLogicalOperator === 'OR';

  if (shouldCreateSeparateRelationships && Array.isArray(portValue)) {
    // Create separate relationships for OR logic with multiple values
    // Note: These will be combined in FilterProcessor.combineFilters()
    portValue.forEach(val => {
      relationships.push({
        label: 'HAS_PORT',
        // Don't use select_one - we want ALL matching ports returned
        target: {
          labels: ['Port'],
          filters: [
            {
              field: portNodeField,
              operator: '=',
              value: val,
              not: isNot,
            },
          ],
        },
      });
    });
  } else {
    // Create single relationship for single value or AND logic
    let finalOperator = operator;
    if (operator === 'IN' || operator === 'NOT IN') {
      finalOperator = '=';
    }

    const relationship = {
      label: 'HAS_PORT',
      // Don't use select_one - we want ALL matching ports returned
      target: {
        labels: ['Port'],
        filters: [
          {
            field: portNodeField,
            operator: finalOperator,
            value: portValue,
            not: isNot,
          },
        ],
      },
    };

    relationships.push(relationship);
  }

  return relationships;
}
